import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ErrorBox } from '../components/DataTable';
import { RecvFields, toRecvInput, type RecvDraft } from '../components/RecvFields';

/**
 * 접수 수정.
 *
 * **신규 등록은 여기 없다** — 접수만 만들어 놓고 노선을 따로 붙이는 경로는
 * "노선 없는 접수"를 만들기 쉬웠다. 신규는 `RecvIntakeForm`(접수 + 노선 한 화면)이 담당한다.
 *
 * 입력칸은 `components/RecvFields` 한 곳에만 있다 — 신규 화면과 같은 칸을 본다.
 * 초기 상태는 '접수'. 이후엔 노선이 붙고 떨어질 때 서버가 자동 재계산한다.
 */
export default function RecvForm({
  recvId,
  onClose,
  onSaved,
}: {
  recvId: number;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [d, setD] = useState<RecvDraft>({ recvType: '일반', isBidding: false, isMeal: false, isLodge: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getRecv({ recvId })
      .then((r) => {
        if (cancelled) return;
        setD({
          recvId: Number(r.recv_id),
          recvType: (r.recv_type as string) ?? '일반',
          custId: r.cust_id === null ? undefined : Number(r.cust_id),
          custName: (r.cust_name as string) ?? undefined,
          recvStartdt: (r.recv_startdt as string) ?? '',
          recvEnddt: (r.recv_enddt as string) ?? '',
          recvUser: (r.recv_user as string) ?? '',
          recvUserEmail: (r.recv_user_email as string) ?? '',
          recvUserPhone: (r.recv_user_phone as string) ?? '',
          recvRequest: (r.recv_request as string) ?? '',
          finManager: (r.fin_manager as string) ?? '',
          finPhone: (r.fin_phone as string) ?? '',
          finReqMethod: (r.fin_req_method as string) ?? '',
          finGrpId: r.fin_grp_id === null || r.fin_grp_id === undefined ? undefined : Number(r.fin_grp_id),
          disptGrp: (r.dispt_grp as string) ?? '',
          // 배차 소속 id(#179) — 레거시도 id 를 같이 적어 두었다(「3, 12, 13」). 없으면 고르기 칸이 이름으로 맞춰 보여 준다.
          disptGrpIds: grpIdsOf(r.dispt_grp_ids),
          finReqLocked: r.fin_req_locked === true,
          billedMons: Array.isArray(r.billed_mons) ? (r.billed_mons as string[]) : [],
          carArea: (r.car_area as string) ?? '',
          isBidding: r.is_bidding === true,
          biddingType: (r.bidding_type as string) ?? '',
          closeDate: (r.close_date as string) ?? '',
          isMeal: r.is_meal === true,
          isLodge: r.is_lodge === true,
          // 담당자를 안 채우면 폼이 빈 값을 보내고, 서버는 그걸 "안 바꿈"으로 읽는다 —
          // 화면에는 '나'로 보이는데 실제로는 예전 담당자가 남아 둘이 갈린다.
          ownerAppUserId: r.owner_app_user_id === null ? undefined : Number(r.owner_app_user_id),
          legacyOwnerLabel: (r.legacy_owner_label as string) ?? undefined,
          // 함께 담당(10-19) — 늘 채워 보낸다(안 채우면 빈 칸으로 보여 「없음」인 척하고, 저장이 그대로 두는지 지우는지 화면과 갈린다).
          coOwnerIds: coOwnersOf(r.co_owners).map((c) => c.id),
          coOwnerLabels: Object.fromEntries(coOwnersOf(r.co_owners).map((c) => [String(c.id), c.label])),
        });
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [recvId]);

  const set = (patch: Partial<RecvDraft>) => setD((cur) => ({ ...cur, ...patch }));

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.updateRecv(toRecvInput(d));
      onSaved(`접수 ${recvId}번을 수정했습니다.`);
    } catch (e) {
      // 서버가 이유를 문장으로 준다(거래처 미선택·마감된 월 등). 그대로 보여준다.
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <div className="card space-y-4 p-5">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-bold">접수 수정 ({recvId}번)</h2>
        <span className="text-xs text-ink-faint">노선은 접수 줄의 “노선 추가”에서 넣습니다</span>
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={saving}>
          취소
        </button>
        <button className="btn-primary" onClick={save} disabled={saving || loading}>
          {saving ? '저장 중…' : '수정'}
        </button>
      </div>

      {error && <ErrorBox message={error} />}
      {loading && <div className="text-sm text-ink-soft">불러오는 중…</div>}

      {!loading && <RecvFields d={d} set={set} />}
    </div>
  );
}

/** getRecv `co_owners`(함께 담당 줄) → id·이름. 모양이 다르면 빈 목록. */
function coOwnersOf(v: unknown): { id: number; label: string }[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => {
      const o = (x ?? {}) as Record<string, unknown>;
      const id = Number(o.app_user_id);
      const name = String(o.name ?? '').trim() || String(o.email ?? '');
      return { id, label: o.is_active === false ? `${name} (비활성)` : name };
    })
    .filter((c) => Number.isInteger(c.id) && c.id > 0);
}

/** 「3, 12, 13」 → [3, 12, 13]. 비었으면 undefined(손대기 전엔 서버가 옛 이름 문자열을 그대로 둔다). */
function grpIdsOf(v: unknown): number[] | undefined {
  if (typeof v !== 'string' || v.trim() === '') return undefined;
  const ids = v.split(',').map((x) => Number(x.trim())).filter((n) => Number.isInteger(n) && n > 0);
  return ids.length ? ids : undefined;
}
